import type {
  LoginFailedProps,
  LoginFailureReasonCode,
  LoginFailureStage,
} from './types';
import type { LoginMethod } from '../authTypes';

export const LOGIN_ANALYTICS_SOURCE = 'auth_screen';

type LoginFailureMetadata = {
  stage: LoginFailureStage;
  reasonCode: LoginFailureReasonCode;
  statusCode?: number;
  providerErrorCode?: string;
  isCancelled?: boolean;
};

type LoginFailureError = Error & {
  loginFailureMetadata?: LoginFailureMetadata;
};

const getErrorMessage = (error: unknown, fallback = 'Login failed'): string =>
  error instanceof Error ? error.message : typeof error === 'string' ? error : fallback;

const extractProviderErrorCode = (message: string): string | undefined => {
  const normalized = message.toLowerCase();
  const patterns = [
    /not displayed:\s*([a-z0-9_:-]+)/i,
    /skipped:\s*([a-z0-9_:-]+)/i,
    /error code[:\s]+([a-z0-9_:-]+)/i,
  ];

  for (const pattern of patterns) {
    const match = message.match(pattern);
    if (match?.[1]) {
      return match[1];
    }
  }

  if (normalized.includes('origin not allowed')) return 'origin_not_allowed';
  if (normalized.includes('third-party cookies')) return 'third_party_cookies_blocked';
  if (normalized.includes('browser blocked')) return 'browser_blocked';
  if (normalized.includes('not loaded')) return 'not_loaded';
  if (normalized.includes('not configured')) return 'not_configured';

  return undefined;
};

const inferProviderReasonCode = (message: string): LoginFailureReasonCode => {
  const normalized = message.toLowerCase();

  if (
    normalized.includes('cancel') ||
    normalized.includes('closed') ||
    normalized.includes('dismissed') ||
    normalized.includes('not displayed') ||
    normalized.includes('skipped')
  ) {
    return 'cancelled';
  }

  if (
    normalized.includes('no credential') ||
    normalized.includes('missing credential') ||
    normalized.includes('no identity token')
  ) {
    return 'missing_credential';
  }

  if (
    normalized.includes('not loaded') ||
    normalized.includes('not configured') ||
    normalized.includes('failed to load') ||
    normalized.includes('origin not allowed') ||
    normalized.includes('browser blocked') ||
    normalized.includes('third-party cookies') ||
    normalized.includes('unavailable')
  ) {
    return 'provider_unavailable';
  }

  return 'unknown_error';
};

export const createLoginFailureError = (
  message: string,
  metadata: LoginFailureMetadata
): Error => Object.assign(new Error(message), { loginFailureMetadata: metadata });

export function buildProviderLoginFailedProps(
  method: LoginMethod,
  errorMessage: string
): LoginFailedProps {
  const reasonCode = inferProviderReasonCode(errorMessage);

  return {
    method,
    source: LOGIN_ANALYTICS_SOURCE,
    stage: 'provider',
    reason_code: reasonCode,
    ...(reasonCode === 'cancelled' ? { is_cancelled: true } : {}),
    ...(extractProviderErrorCode(errorMessage)
      ? { provider_error_code: extractProviderErrorCode(errorMessage) }
      : {}),
    error_message: errorMessage,
  };
}

export function buildRuntimeLoginFailedProps(
  method: LoginMethod,
  error: unknown,
  fallbackStage: LoginFailureStage = 'backend'
): LoginFailedProps {
  const errorMessage = getErrorMessage(error);
  const metadata = (error instanceof Error ? (error as LoginFailureError).loginFailureMetadata : undefined);
  const inferredReasonCode = (() => {
    if (metadata?.reasonCode) return metadata.reasonCode;
    if (error instanceof DOMException && error.name === 'AbortError') return 'timeout';
    if (error instanceof TypeError) return 'network_error';
    if (fallbackStage === 'identify') return 'identify_failed';
    if (fallbackStage === 'backend') return 'backend_rejected';
    return 'unknown_error';
  })();

  return {
    method,
    source: LOGIN_ANALYTICS_SOURCE,
    stage: metadata?.stage ?? fallbackStage,
    reason_code: inferredReasonCode,
    ...(typeof metadata?.statusCode === 'number' ? { status_code: metadata.statusCode } : {}),
    ...(metadata?.providerErrorCode ? { provider_error_code: metadata.providerErrorCode } : {}),
    ...(metadata?.isCancelled ? { is_cancelled: true } : {}),
    error_message: errorMessage,
  };
}
